/**
 * GoalProgressWidgetPage — goal-progress report.
 *
 * Layout:
 *   topbar ("Goal Progress")
 *   → 4 summary tiles (active, avg %, at-risk, completed this month)
 *   → goal-type filter
 *   → grid of progress cards (name, bar, target, ETA, status chip)
 *
 * Accent: green-600/700/50 only. Neutrals: white + zinc-200 + zinc-900.
 * Typography: Inter (inherits from app). Radius: 6-8px. No emoji, no dark
 * mode, no competitor chrome. Icons are inline SVG.
 */
import { useMemo, useState } from "react";
import {
	useGoalProgressWidgetQuery,
} from "./hooks";
import type {
	GoalProgressWidgetRow,
	GoalProgressSummary,
} from "./api";

// ---------------------------------------------------------------------------
// Visual primitives
// ---------------------------------------------------------------------------

const CARD =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm";
const TILE =
	"rounded-[8px] border border-zinc-200 bg-white p-5 shadow-sm flex flex-col gap-1";
const CHIP_BASE =
	"inline-flex items-center gap-1 rounded-[6px] px-2 py-0.5 text-xs font-medium";

type StatusKey = "on_track" | "at_risk" | "behind" | "completed";

function statusChipClass(status: string | undefined): string {
	switch (status) {
		case "on_track":
			return `${CHIP_BASE} bg-green-50 text-green-700 border border-green-200`;
		case "completed":
			return `${CHIP_BASE} bg-green-600 text-white`;
		case "at_risk":
			return `${CHIP_BASE} bg-amber-50 text-amber-700 border border-amber-200`;
		case "behind":
			return `${CHIP_BASE} bg-red-50 text-red-700 border border-red-200`;
		default:
			return `${CHIP_BASE} bg-zinc-100 text-zinc-700 border border-zinc-200`;
	}
}

function statusLabel(status: string | undefined): string {
	switch (status) {
		case "on_track":
			return "On track";
		case "at_risk":
			return "At risk";
		case "behind":
			return "Behind";
		case "completed":
			return "Completed";
		default:
			return status ? String(status) : "—";
	}
}

function clampPct(n: number): number {
	if (!Number.isFinite(n)) return 0;
	return Math.max(0, Math.min(100, Math.round(n)));
}

function derivePct(row: GoalProgressWidgetRow): number {
	if (typeof row.progress === "number") return clampPct(row.progress);
	if (
		typeof row.current === "number" &&
		typeof row.target === "number" &&
		row.target > 0
	) {
		return clampPct((row.current / row.target) * 100);
	}
	return 0;
}

function formatNumber(v: unknown): string {
	if (typeof v === "number" && Number.isFinite(v)) {
		return v.toLocaleString();
	}
	return "—";
}

function formatEta(v: unknown): string {
	if (!v) return "—";
	if (typeof v === "string") return v;
	if (typeof v === "number" && Number.isFinite(v)) {
		try {
			return new Date(v).toLocaleDateString();
		} catch {
			return String(v);
		}
	}
	return "—";
}

// ---------------------------------------------------------------------------
// Inline icons (green-600). No emoji, no svg library.
// ---------------------------------------------------------------------------

function IconTarget() {
	return (
		<svg
			className="h-4 w-4 text-green-600"
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.75"
			aria-hidden="true"
		>
			<circle cx="10" cy="10" r="7" />
			<circle cx="10" cy="10" r="3.5" />
			<circle cx="10" cy="10" r="0.9" fill="currentColor" />
		</svg>
	);
}

function IconTrend() {
	return (
		<svg
			className="h-4 w-4 text-green-600"
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.75"
			aria-hidden="true"
		>
			<path d="M3 14l4-4 3 3 5-6" strokeLinecap="round" strokeLinejoin="round" />
			<path d="M12 7h4v4" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	);
}

function IconWarn() {
	return (
		<svg
			className="h-4 w-4 text-green-600"
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.75"
			aria-hidden="true"
		>
			<path
				d="M10 3l7.5 13h-15L10 3z"
				strokeLinejoin="round"
				strokeLinecap="round"
			/>
			<path d="M10 8v4" strokeLinecap="round" />
			<circle cx="10" cy="14.25" r="0.8" fill="currentColor" />
		</svg>
	);
}

function IconCheck() {
	return (
		<svg
			className="h-4 w-4 text-green-600"
			viewBox="0 0 20 20"
			fill="none"
			stroke="currentColor"
			strokeWidth="1.75"
			aria-hidden="true"
		>
			<circle cx="10" cy="10" r="7.5" />
			<path d="M7 10.2l2.2 2.2L13.5 8" strokeLinecap="round" strokeLinejoin="round" />
		</svg>
	);
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface TileProps {
	label: string;
	value: string;
	icon: React.ReactNode;
	testId: string;
}

function Tile({ label, value, icon, testId }: TileProps) {
	return (
		<div className={TILE} data-testid={testId}>
			<div className="flex items-center justify-between">
				<span className="text-xs font-medium uppercase tracking-wider text-zinc-500">
					{label}
				</span>
				<span className="flex h-8 w-8 items-center justify-center rounded-[6px] bg-green-50">
					{icon}
				</span>
			</div>
			<div className="text-2xl font-semibold tabular-nums text-zinc-900">
				{value}
			</div>
		</div>
	);
}

interface ProgressCardProps {
	row: GoalProgressWidgetRow;
}

function ProgressCard({ row }: ProgressCardProps) {
	const pct = derivePct(row);
	const status =
		typeof row.status === "string"
			? (row.status as StatusKey)
			: pct >= 100
				? "completed"
				: pct >= 70
					? "on_track"
					: pct >= 40
						? "at_risk"
						: "behind";

	return (
		<article
			className={CARD}
			data-testid={`goal-progress-widget-card-${row.id}`}
		>
			<div className="flex items-start justify-between gap-3">
				<div className="flex flex-col gap-0.5">
					<h3 className="text-sm font-semibold text-zinc-900">
						{row.name ?? row.id}
					</h3>
					<p className="text-xs text-zinc-500">
						{row.goal_type ?? "goal"}
					</p>
				</div>
				<span
					className={statusChipClass(status)}
					data-testid={`goal-progress-widget-status-${row.id}`}
				>
					{statusLabel(status)}
				</span>
			</div>

			<div className="mt-4 flex flex-col gap-1.5">
				<div className="flex items-center justify-between text-xs text-zinc-600">
					<span>Progress</span>
					<span className="font-medium tabular-nums text-zinc-900">
						{pct}%
					</span>
				</div>
				<div
					className="h-2 w-full overflow-hidden rounded-[6px] bg-zinc-100"
					role="progressbar"
					aria-valuenow={pct}
					aria-valuemin={0}
					aria-valuemax={100}
					aria-label={`${row.name ?? row.id} progress`}
					data-testid={`goal-progress-widget-bar-${row.id}`}
				>
					<div
						className="h-full rounded-[6px] bg-green-600 transition-[width] duration-300"
						style={{ width: `${pct}%` }}
					/>
				</div>
			</div>

			<dl className="mt-4 grid grid-cols-2 gap-3 text-xs">
				<div className="flex flex-col gap-0.5">
					<dt className="uppercase tracking-wider text-zinc-500">
						Target
					</dt>
					<dd className="font-medium tabular-nums text-zinc-900">
						{formatNumber(row.target)}
					</dd>
				</div>
				<div className="flex flex-col gap-0.5">
					<dt className="uppercase tracking-wider text-zinc-500">ETA</dt>
					<dd className="font-medium text-zinc-900">
						{formatEta(row.eta)}
					</dd>
				</div>
			</dl>
		</article>
	);
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const GOAL_TYPES: Array<{ value: string; label: string }> = [
	{ value: "all", label: "All types" },
	{ value: "revenue", label: "Revenue" },
	{ value: "conversions", label: "Conversions" },
	{ value: "installs", label: "Installs" },
	{ value: "engagement", label: "Engagement" },
];

export function GoalProgressWidgetPage() {
	const [goalType, setGoalType] = useState<string>("all");
	const query = useGoalProgressWidgetQuery({ goal_type: goalType });

	const items = query.data?.items ?? [];

	const summary: GoalProgressSummary = useMemo(() => {
		if (query.data?.summary) return query.data.summary;
		if (!items.length) {
			return {
				active_goals: 0,
				avg_progress: 0,
				at_risk: 0,
				completed_this_month: 0,
			};
		}
		const pcts = items.map(derivePct);
		const avg = pcts.reduce((a, b) => a + b, 0) / pcts.length;
		const atRisk = items.filter(
			(r) => r.status === "at_risk" || r.status === "behind",
		).length;
		const completed = items.filter((r) => r.status === "completed").length;
		return {
			active_goals: items.filter((r) => r.status !== "completed").length,
			avg_progress: Math.round(avg),
			at_risk: atRisk,
			completed_this_month: completed,
		};
	}, [query.data, items]);

	return (
		<main
			data-testid="goal-progress-widget-page"
			className="mx-auto flex w-full max-w-6xl flex-col gap-6 px-6 py-8"
		>
			{/* Topbar */}
			<header className="flex flex-col gap-1 border-b border-zinc-200 pb-5">
				<p className="text-xs font-semibold uppercase tracking-wider text-green-700">
					Report
				</p>
				<h1 className="text-2xl font-semibold text-zinc-900">
					Goal Progress
				</h1>
				<p className="text-sm text-zinc-600">
					Track every goal's progress toward its target in one view.
				</p>
			</header>

			{/* Summary tiles */}
			<section
				className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4"
				aria-label="Goal progress summary"
				data-testid="goal-progress-widget-summary"
			>
				<Tile
					label="Active goals"
					value={formatNumber(summary.active_goals)}
					icon={<IconTarget />}
					testId="goal-progress-widget-tile-active"
				/>
				<Tile
					label="Avg progress"
					value={`${clampPct(summary.avg_progress)}%`}
					icon={<IconTrend />}
					testId="goal-progress-widget-tile-avg"
				/>
				<Tile
					label="At risk"
					value={formatNumber(summary.at_risk)}
					icon={<IconWarn />}
					testId="goal-progress-widget-tile-risk"
				/>
				<Tile
					label="Completed this month"
					value={formatNumber(summary.completed_this_month)}
					icon={<IconCheck />}
					testId="goal-progress-widget-tile-completed"
				/>
			</section>

			{/* Filter row */}
			<section className="flex flex-wrap items-center justify-between gap-3">
				<div className="flex items-center gap-2">
					<label
						htmlFor="goal-progress-widget-goal-type"
						className="text-xs font-medium uppercase tracking-wider text-zinc-500"
					>
						Goal type
					</label>
					<select
						id="goal-progress-widget-goal-type"
						data-testid="goal-progress-widget-filter"
						className="rounded-[6px] border border-zinc-200 bg-white px-3 py-1.5 text-sm text-zinc-900 shadow-sm focus:border-green-600 focus:outline-none focus:ring-1 focus:ring-green-600"
						value={goalType}
						onChange={(e) => setGoalType(e.target.value)}
					>
						{GOAL_TYPES.map((opt) => (
							<option key={opt.value} value={opt.value}>
								{opt.label}
							</option>
						))}
					</select>
				</div>
				{query.isFetching && !query.isLoading ? (
					<span
						className="text-xs text-zinc-500"
						data-testid="goal-progress-widget-refetching"
					>
						Refreshing…
					</span>
				) : null}
			</section>

			{/* Body states */}
			{query.isLoading ? (
				<section
					className={CARD}
					data-testid="goal-progress-widget-loading"
				>
					<p className="text-sm text-zinc-500">Loading goals.</p>
				</section>
			) : query.error ? (
				<section
					className={`${CARD} border-red-200 bg-red-50`}
					role="alert"
					data-testid="goal-progress-widget-error"
				>
					<p className="text-sm font-medium text-red-700">
						{query.error.message}
					</p>
				</section>
			) : items.length === 0 ? (
				<section
					className={CARD}
					data-testid="goal-progress-widget-empty"
				>
					<p className="text-sm text-zinc-500">
						No goals match the current filter.
					</p>
				</section>
			) : (
				<section
					className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3"
					aria-label="Goals"
					data-testid="goal-progress-widget-grid"
				>
					{items.map((row) => (
						<ProgressCard key={row.id} row={row} />
					))}
				</section>
			)}
		</main>
	);
}

export default GoalProgressWidgetPage;
